import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid-pro';
import { getColumns, Column } from '../../api/spreadsheet';
import { useSnackbar } from '../../context/SnackbarContext';
import { useSpreadsheetViewType } from '../../context/CampaignContext';
import { CampaignProspectType } from '../../api/campaigns';
import ModalHeader from './CSVUploadModal/ModalHeader';
import ModalActions from './CSVUploadModal/ModalActions';
import { UploadStep } from './CSVUploadModal/ModalActions';
import PreviewMappingStep from './CSVUploadModal/PreviewMappingStep';
import type { SpreadsheetColumnTypeMap } from '../../constants/spreadsheetColumnTypes';
import ProspectDatabaseSearchForm from '../Prospect/ProspectDatabaseSearchForm';

enum ProspectDbImportStep {
  SEARCH = 'SEARCH',
  PREVIEW_MAPPING = 'PREVIEW_MAPPING'
}

interface ProspectDatabaseImportModalProps {
  open: boolean;
  onClose: () => void;
  prospectType: CampaignProspectType;
  onMappingComplete: (
    uploadedRows: Record<string, any>[],
    mapping: Record<string, string | null>,
    customColumnTypes?: SpreadsheetColumnTypeMap
  ) => void;
}

const ProspectDatabaseImportModal: React.FC<
  ProspectDatabaseImportModalProps
> = ({ open, onClose, prospectType, onMappingComplete }) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const viewType = useSpreadsheetViewType();
  const { showSnackbar } = useSnackbar();
  const [step, setStep] = useState<ProspectDbImportStep>(
    ProspectDbImportStep.SEARCH
  );
  const [searchData, setSearchData] = useState<Record<string, any>[]>([]);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [columnsDefinition, setColumnsDefinition] = useState<GridColDef[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [currentMapping, setCurrentMapping] = useState<
    Record<string, string | null>
  >({});
  const [customColumnTypes, setCustomColumnTypes] =
    useState<SpreadsheetColumnTypeMap>({});
  const [existingColumnTypes, setExistingColumnTypes] = useState<
    Record<string, string>
  >({});
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (open && campaignId) {
      const fetchColumns = async () => {
        setLoadingColumns(true);
        try {
          const response = await getColumns(campaignId, viewType);
          const columns: Column[] = (response.columns || []).sort(
            (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
          );
          const gridCols: GridColDef[] = columns.map((col) => ({
            field: col.col_name,
            headerName: col.col_name,
            width: 150,
            sortable: true,
            filterable: false,
            editable: false
          }));
          setColumnsDefinition(gridCols);
          const typesMap: Record<string, string> = {};
          columns.forEach((col) => {
            if (col.col_type) typesMap[col.col_name] = col.col_type;
          });
          setExistingColumnTypes(typesMap);
        } catch (error: any) {
          showSnackbar(
            error?.message || 'Failed to load campaign columns',
            'error'
          );
        } finally {
          setLoadingColumns(false);
        }
      };
      fetchColumns();
    }
  }, [open, campaignId, viewType, showSnackbar]);

  const resetState = () => {
    setStep(ProspectDbImportStep.SEARCH);
    setSearchData([]);
    setDetectedColumns([]);
    setCurrentMapping({});
    setCustomColumnTypes({});
    setExistingColumnTypes({});
  };

  const handleSearchConfirm = (rows: Record<string, any>[]) => {
    if (!rows || rows.length === 0) {
      showSnackbar('No data to map', 'error');
      return;
    }
    setSearchData(rows);
    setDetectedColumns(Object.keys(rows[0] || {}));
    setStep(ProspectDbImportStep.PREVIEW_MAPPING);
  };

  const handleMappingChange = (mapping: Record<string, string | null>) => {
    setCurrentMapping(mapping);
  };

  const handleMappingConfirmed = async () => {
    const allFields = Object.keys(currentMapping);

    const uploadedRows = searchData.map((row) => {
      const newRow: Record<string, any> = {};
      allFields.forEach((field) => {
        const sourceCol = currentMapping[field];
        newRow[field] = sourceCol ? (row[sourceCol] ?? '') : '';
      });
      return newRow;
    });

    setImporting(true);
    try {
      await onMappingComplete(uploadedRows, currentMapping, customColumnTypes);
      resetState();
      onClose();
    } finally {
      setImporting(false);
    }
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    if (step === ProspectDbImportStep.PREVIEW_MAPPING) {
      setStep(ProspectDbImportStep.SEARCH);
      setSearchData([]);
      setDetectedColumns([]);
      setCurrentMapping({});
    }
  };

  const headers = searchData.length > 0 ? Object.keys(searchData[0]) : [];
  const rawCsvRows = [
    headers,
    ...searchData.map((row) => headers.map((h) => String(row[h] ?? '')))
  ];

  return (
    <Dialog
      open={open}
      onClose={importing ? () => {} : handleCancel}
      maxWidth={step === ProspectDbImportStep.PREVIEW_MAPPING ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
          maxHeight: '90vh',
          position: 'relative'
        }
      }}
    >
      {importing && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.85)',
            zIndex: 10,
            borderRadius: '20px'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">Importing data...</Typography>
          </Box>
        </Box>
      )}
      <ModalHeader
        title={
          step === ProspectDbImportStep.SEARCH
            ? 'Search Prospect Database'
            : 'Preview & Map Columns'
        }
        onClose={importing ? () => {} : handleCancel}
      />
      <DialogContent sx={{ px: 3, py: 2, '&:first-of-type': { pt: 2 } }}>
        {step === ProspectDbImportStep.SEARCH && (
          <Box>
            <ProspectDatabaseSearchForm
              onConfirm={handleSearchConfirm}
              onCancel={handleCancel}
            />
          </Box>
        )}

        {step === ProspectDbImportStep.PREVIEW_MAPPING && (
          <PreviewMappingStep
            rawCsvRows={rawCsvRows}
            headerRowOffset={0}
            onHeaderRowChange={() => {}}
            columnsDefinition={columnsDefinition}
            existingColumnTypes={existingColumnTypes}
            detectedColumns={detectedColumns}
            loadingColumns={loadingColumns}
            onMappingChange={handleMappingChange}
            onColumnTypesChange={setCustomColumnTypes}
          />
        )}
      </DialogContent>
      {step === ProspectDbImportStep.PREVIEW_MAPPING && (
        <ModalActions
          step={UploadStep.PREVIEW_MAPPING}
          onCancel={handleCancel}
          onBack={handleBack}
          onConfirm={handleMappingConfirmed}
          importing={importing}
        />
      )}
    </Dialog>
  );
};

export default ProspectDatabaseImportModal;
